"use client"

import * as React from "react"

/** Persisted top-left position (viewport px). Null until the user drags the FAB for the first time. */
type FabPosition = { x: number; y: number }

const STORAGE_KEY = "ibada.rafiki.fab.position"
/** Pointer travel (px) before a press becomes a drag — below this it's treated as a tap (open Rafiki). */
const DRAG_THRESHOLD = 6
/** Keep the button fully on-screen with a small breathing margin from every edge. */
const EDGE_MARGIN = 12

function readStored(): FabPosition | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<FabPosition>
    if (typeof parsed?.x !== "number" || typeof parsed?.y !== "number") return null
    return { x: parsed.x, y: parsed.y }
  } catch {
    return null
  }
}

/** Clamp a top-left position so a `size`×`size` button stays within the current viewport (minus the margin). */
function clamp(position: FabPosition, size: number): FabPosition {
  const maxX = Math.max(EDGE_MARGIN, window.innerWidth - size - EDGE_MARGIN)
  const maxY = Math.max(EDGE_MARGIN, window.innerHeight - size - EDGE_MARGIN)
  return {
    x: Math.min(Math.max(position.x, EDGE_MARGIN), maxX),
    y: Math.min(Math.max(position.y, EDGE_MARGIN), maxY),
  }
}

/**
 * Make a fixed FAB draggable via pointer events, persisting where it's dropped. A press that moves less than
 * {@link DRAG_THRESHOLD} stays a tap (so the button still opens on its native click); anything more repositions
 * it and suppresses the trailing click. The move/up handlers live on `window` (NOT pointer capture) — capturing
 * the pointer in `pointerdown` swallows the subsequent click on some mobile browsers, which would force a
 * double-tap to open. Returns inline `style` (top-left positioning once moved) and the pointer-down handler to
 * spread on the button. When `enabled` is false (e.g. desktop) it's inert and the FAB keeps its CSS anchoring.
 */
export function useDraggableFab({ enabled, size }: { enabled: boolean; size: number }) {
  const [position, setPosition] = React.useState<FabPosition | null>(null)
  const [isDragging, setIsDragging] = React.useState(false)
  // True between a drag-end and the synthetic click it produces — lets the click handler skip the open/close.
  const suppressClickRef = React.useRef(false)
  // Drag bookkeeping (refs so the window listeners never read stale state): pointer start + the offset from the
  // button's top-left to the pointer, plus whether the threshold has been crossed.
  const dragRef = React.useRef<{ startX: number; startY: number; offsetX: number; offsetY: number } | null>(null)
  const draggingRef = React.useRef(false)

  // Load the saved position once enabled, and re-clamp it on viewport resize / rotation.
  React.useEffect(() => {
    if (!enabled) return
    const stored = readStored()
    if (stored) setPosition(clamp(stored, size))
    const onResize = () => setPosition((current) => (current ? clamp(current, size) : current))
    window.addEventListener("resize", onResize)
    window.addEventListener("orientationchange", onResize)
    return () => {
      window.removeEventListener("resize", onResize)
      window.removeEventListener("orientationchange", onResize)
    }
  }, [enabled, size])

  // Global move/up tracking (active only while enabled). They no-op until a pointerdown on the button arms
  // `dragRef`, so they're cheap when idle.
  React.useEffect(() => {
    if (!enabled) return

    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const movedX = event.clientX - drag.startX
      const movedY = event.clientY - drag.startY
      if (!draggingRef.current && Math.hypot(movedX, movedY) < DRAG_THRESHOLD) return
      if (!draggingRef.current) {
        draggingRef.current = true
        setIsDragging(true)
      }
      // Stop the page from scrolling under the finger while panning.
      if (event.cancelable) event.preventDefault()
      setPosition(clamp({ x: event.clientX - drag.offsetX, y: event.clientY - drag.offsetY }, size))
    }

    const onUp = () => {
      const wasDragging = draggingRef.current
      dragRef.current = null
      draggingRef.current = false
      if (!wasDragging) return
      // It was a real drag — swallow the trailing click (if the browser even fires one after a drag) and
      // persist the final spot. A tap dispatches its click synchronously before this `setTimeout(0)` runs, so
      // the toggle consumes the flag first; when a drag fires NO click, the timeout clears it so the flag never
      // lingers and eats the next genuine tap.
      suppressClickRef.current = true
      window.setTimeout(() => {
        suppressClickRef.current = false
      }, 0)
      setIsDragging(false)
      setPosition((current) => {
        if (current) {
          try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
          } catch {
            // Ignore storage failures (private mode / quota) — position just won't persist.
          }
        }
        return current
      })
    }

    window.addEventListener("pointermove", onMove, { passive: false })
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onUp)
    }
  }, [enabled, size])

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return
      const rect = event.currentTarget.getBoundingClientRect()
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
      }
    },
    [enabled],
  )

  const style: React.CSSProperties =
    enabled && position ? { left: position.x, top: position.y, right: "auto", bottom: "auto" } : {}

  return { style, isDragging, suppressClickRef, handlers: { onPointerDown } }
}
